import React, { useState } from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';
import CSSTransition from 'react-transition-group/CSSTransition';

import styles from './UserDataRow.module.scss';
import fadeAnimation from '../../../components/animations/fadeAnimation.module.scss';
import IconButton from '../../../components/IconButton';
import OutsideClickHandler from '../../../components/OutsideClickHandler';
import TableDataCell from '../../../components/table/TableDataCell';
import { getRoleNamesByIds } from '../../constants';
import UserEditMenu from '../UserEditMenu/UserEditMenu';

const UserDataRow = ({
  active,
  id,
  fullName,
  username,
  permissionGroup,
  openEdit,
  openDeactivate,
  isAdmin,
  roles,
  openActivate,
}) => {
  const [menuOpened, setMenuOpened] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const data = { active, id, fullName, username, permissionGroup, roles };

  return (
    <tr className={cx(styles.wrapper)}>
      <TableDataCell className='ps-5'>{id}</TableDataCell>
      <TableDataCell className='py-3'>{fullName || '--'}</TableDataCell>
      <TableDataCell className='py-3'>{username}</TableDataCell>
      <TableDataCell className='py-3'>
        <span>
          {active ? (
            <i className='icon-verified1-fill text-green me-2' />
          ) : (
            <i className='icon-not-verified-fill text-red me-2' />
          )}
          {active ? 'Active user' : 'Inactive user'}
        </span>
      </TableDataCell>
      <TableDataCell className='py-3'>
        <div className='d-flex justify-content-between me-3'>
          <div>{getRoleNamesByIds(permissionGroup)}</div>
          {isAdmin && (
            <div className='position-relative'>
              <div className={('w-100', 'd-flex', 'align-items-center', 'justify-content-end')}>
                <IconButton
                  icon={loadingAction ? 'icon-loading icon-spin' : 'icon-more-horizontal'}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!loadingAction) {
                      setMenuOpened(true);
                    }
                  }}
                  disabled={!!loadingAction}
                />
              </div>
              <CSSTransition
                mountOnEnter
                unmountOnExit
                in={menuOpened}
                timeout={200}
                classNames={{
                  enter: fadeAnimation.enter,
                  enterActive: fadeAnimation.enterActive,
                  exit: fadeAnimation.exit,
                  exitActive: fadeAnimation.exitActive,
                }}
              >
                <OutsideClickHandler
                  clickHandler={() => {
                    setMenuOpened(false);
                  }}
                >
                  <UserEditMenu
                    onDeactivate={() => {
                      setLoadingAction('deactivate');
                      openDeactivate(data);
                      setMenuOpened(false);
                      setTimeout(() => setLoadingAction(null), 1000);
                    }}
                    onEdit={() => {
                      setLoadingAction('edit');
                      openEdit(data);
                      setMenuOpened(false);
                      setTimeout(() => setLoadingAction(null), 500);
                    }}
                    status={active}
                    onActivate={() => {
                      setLoadingAction('activate');
                      openActivate(data);
                      setMenuOpened(false);
                      setTimeout(() => setLoadingAction(null), 1000);
                    }}
                    loadingAction={loadingAction}
                  />
                </OutsideClickHandler>
              </CSSTransition>
            </div>
          )}
        </div>
      </TableDataCell>
    </tr>
  );
};

UserDataRow.propTypes = {
  active: PropTypes.bool.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fullName: PropTypes.string,
  username: PropTypes.string.isRequired,
  permissionGroup: PropTypes.arrayOf(PropTypes.string).isRequired,
  openEdit: PropTypes.func.isRequired,
  openDeactivate: PropTypes.func.isRequired,
  openActivate: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  roles: PropTypes.array,
};

UserDataRow.defaultProps = {
  fullName: '',
  roles: [],
};

export default UserDataRow;
